"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { apiGet, apiPatch, apiPost, type Cliente, type Opcion, type Producto } from "@/lib/api";
import { mensajeError } from "@/lib/errores";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const NUEVA_FAMILIA = "__nueva__";

/** Alta (sin `producto`) o edición de la ficha de un producto. */
export function ProductoDialog({
  open,
  onOpenChange,
  producto,
  onGuardado,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  producto?: Producto;
  onGuardado: (p: Producto) => void;
}) {
  const t = useTranslations("productoForm");
  const tc = useTranslations("common");
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [familias, setFamilias] = useState<Opcion[]>([]);
  const [usuarios, setUsuarios] = useState<Opcion[]>([]);
  const [nombre, setNombre] = useState("");
  const [codigo, setCodigo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [clienteId, setClienteId] = useState("");
  const [familiaId, setFamiliaId] = useState("");
  const [familiaNueva, setFamiliaNueva] = useState("");
  const [responsableId, setResponsableId] = useState("");
  const [versionInicial, setVersionInicial] = useState("1.0");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNombre(producto?.nombre ?? "");
    setCodigo(producto?.codigo ?? "");
    setDescripcion(producto?.descripcion ?? "");
    setClienteId(producto?.cliente_id ?? "");
    setFamiliaId(producto?.familia_id ?? "");
    setFamiliaNueva("");
    setResponsableId(producto?.responsable_id ?? "");
    setVersionInicial("1.0");
    setError(null);
    Promise.all([
      apiGet<Cliente[]>("/clientes"),
      apiGet<Opcion[]>("/listas/familias"),
      apiGet<Opcion[]>("/usuarios/basico"),
    ])
      .then(([c, f, u]) => {
        setClientes(c);
        setFamilias(f);
        setUsuarios(u);
      })
      .catch((e) => setError(mensajeError(e)));
  }, [open, producto]);

  async function guardar() {
    setError(null);
    if (!nombre.trim()) return setError(t("nombreObligatorio"));
    setBusy(true);
    try {
      let familia = familiaId;
      if (familiaId === NUEVA_FAMILIA) {
        if (!familiaNueva.trim()) throw new Error(t("familiaNuevaObligatoria"));
        familia = (await apiPost<Opcion>("/listas/familias", { nombre: familiaNueva.trim() })).id;
      }
      const body = {
        nombre: nombre.trim(),
        codigo: codigo.trim() || null,
        descripcion: descripcion.trim() || null,
        cliente_id: clienteId || null,
        familia_id: familia || null,
        responsable_id: responsableId || null,
      };
      const p = producto
        ? await apiPatch<Producto>(`/productos/${producto.id}`, body)
        : await apiPost<Producto>("/productos", { ...body, version_inicial: versionInicial.trim() || "1.0" });
      onGuardado(p);
      onOpenChange(false);
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{producto ? t("editar") : t("nuevo")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="p-nombre">{t("nombre")} *</Label>
            <Input id="p-nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="p-codigo">{t("codigo")}</Label>
            <Input id="p-codigo" value={codigo} onChange={(e) => setCodigo(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="p-cliente">{t("cliente")}</Label>
            <NativeSelect
              id="p-cliente"
              className="w-full"
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
            >
              <option value="">{t("sinCliente")}</option>
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div className="space-y-2">
            <Label htmlFor="p-familia">{t("familia")}</Label>
            <NativeSelect
              id="p-familia"
              className="w-full"
              value={familiaId}
              onChange={(e) => setFamiliaId(e.target.value)}
            >
              <option value="">—</option>
              {familias.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.nombre}
                </option>
              ))}
              <option value={NUEVA_FAMILIA}>{t("familiaNueva")}</option>
            </NativeSelect>
            {familiaId === NUEVA_FAMILIA && (
              <Input
                placeholder={t("familiaNuevaNombre")}
                value={familiaNueva}
                onChange={(e) => setFamiliaNueva(e.target.value)}
              />
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="p-resp">{t("responsable")}</Label>
            <NativeSelect
              id="p-resp"
              className="w-full"
              value={responsableId}
              onChange={(e) => setResponsableId(e.target.value)}
            >
              <option value="">{producto ? "—" : t("yo")}</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </NativeSelect>
          </div>
          {!producto && (
            <div className="space-y-2">
              <Label htmlFor="p-vini">{t("versionInicial")}</Label>
              <Input
                id="p-vini"
                value={versionInicial}
                onChange={(e) => setVersionInicial(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="p-desc">{t("descripcion")}</Label>
            <Textarea id="p-desc" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </div>
        </div>
        {!producto && <p className="text-xs text-muted-foreground">{t("ayudaAlta")}</p>}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {tc("cancelar")}
          </Button>
          <Button onClick={guardar} disabled={busy}>
            {busy ? tc("guardando") : tc("guardar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
